import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Button, 
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    InputAdornment,
    useTheme,
    alpha,
    Snackbar,
    Alert
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';

import careService from '../services/careService';
import { CareDocumentDTO, CareDocumentType } from '../types/crm.types';

const CareDocumentsPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<CareDocumentDTO[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    useEffect(() => {
        const fetchDocs = async () => {
            try {
                const data = await careService.getDocuments();
                setDocuments(data);
            } catch (error) {
                console.error('Failed to fetch documents', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDocs();
    }, []);

    const handleAction = (msg: string) => {
        setSnackbar({ open: true, message: msg });
    };

    const getTypeColor = (type: CareDocumentType) => {
        switch (type) {
            case CareDocumentType.MEDICAL_RECORD: return theme.palette.error.main;
            case CareDocumentType.CONSENT_FORM: return theme.palette.info.main;
            case CareDocumentType.INSURANCE: return theme.palette.success.main;
            case CareDocumentType.ADMISSION_AGREEMENT: return theme.palette.warning.main;
            default: return theme.palette.grey[500];
        }
    };

    const filteredDocs = documents.filter(doc => 
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" color="text.primary">
                        Care Documents
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Central repository for resident medical records, consent forms, and admission agreements.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    startIcon={<FileUploadIcon />} 
                    onClick={() => handleAction('Upload dialog coming soon')}
                    sx={{ borderRadius: 2, fontWeight: '700' }}
                >
                    Upload Document
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: 1, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search documents by title or type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Paper>

            {loading ? (
                <Box display="flex" justifyContent="center" py={10}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableRow>
                                <TableCell><Typography fontWeight="700">Document Name</Typography></TableCell>
                                <TableCell><Typography fontWeight="700">Type</Typography></TableCell>
                                <TableCell><Typography fontWeight="700">Uploaded Date</Typography></TableCell>
                                <TableCell><Typography fontWeight="700">Size</Typography></TableCell>
                                <TableCell align="right"><Typography fontWeight="700">Actions</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDocs.map((doc) => (
                                <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.01) } }}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <DescriptionIcon sx={{ color: getTypeColor(doc.type) }} />
                                            <Typography variant="body2" fontWeight="700">{doc.title}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={doc.type.replace('_', ' ')} 
                                            size="small" 
                                            sx={{ 
                                                fontWeight: '800', 
                                                color: '#fff', 
                                                bgcolor: getTypeColor(doc.type),
                                                borderRadius: 1.5
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{new Date(doc.createdAt).toLocaleDateString()}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{doc.fileSize}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" justifyContent="flex-end">
                                            <IconButton size="small" onClick={() => handleAction('Previewing document...')}><VisibilityIcon fontSize="small" /></IconButton>
                                            <IconButton size="small" onClick={() => handleAction('Downloading document...')}><DownloadIcon fontSize="small" /></IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredDocs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                        <Typography variant="body2" color="text.disabled">No documents found matching your search.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={3000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CareDocumentsPage;
